import React from 'react';
import { Box, Checkbox, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { BRAND_LUXE_MUTED, BRAND_LUXE_PRIMARY } from '../brandDashboardTokens';
import { ManageGoldButton, ManageSurface } from './manageShared';

const ROLES = [
    { key: 'viewer', label: 'Viewer', color: 'rgba(255,255,255,0.12)', desc: 'Read-only access' },
    {
        key: 'content_strategist',
        label: 'Strategist',
        color: 'rgba(100,180,255,0.18)',
        desc: 'Plans & proposes content',
    },
    {
        key: 'content_ops_manager',
        label: 'Ops Manager',
        color: 'rgba(255,180,50,0.18)',
        desc: 'Manages social & payroll',
    },
    { key: 'brand_admin', label: 'Brand Admin', color: 'rgba(160,100,255,0.18)', desc: 'Full brand access' },
] as const;

type RoleKey = (typeof ROLES)[number]['key'];

const PERMISSION_GROUPS = [
    {
        group: 'View',
        description: 'What the role can read',
        items: [
            { key: 'view', label: 'Dashboard' },
            { key: 'view_calendar', label: 'Calendar' },
            { key: 'view_contracts', label: 'Contracts' },
            { key: 'view_team', label: 'Team' },
            { key: 'view_settings', label: 'Settings' },
            { key: 'view_payroll', label: 'Payroll' },
            { key: 'view_strikes', label: 'Strikes' },
        ],
    },
    {
        group: 'Calendar',
        description: 'Content scheduling actions',
        items: [
            { key: 'edit_calendar', label: 'Edit posts' },
            { key: 'propose_calendar', label: 'Propose posts' },
            { key: 'publish_calendar', label: 'Publish posts' },
            { key: 'approve_calendar', label: 'Approve posts' },
        ],
    },
    {
        group: 'Manage',
        description: 'Administrative actions',
        items: [
            { key: 'manage_social', label: 'Social accounts' },
            { key: 'manage_team', label: 'Team members' },
            { key: 'manage_contracts', label: 'Contracts' },
            { key: 'manage_payroll', label: 'Payroll' },
            { key: 'manage_strikes', label: 'Strikes' },
            { key: 'manage_settings', label: 'Settings' },
            { key: 'freeze_contract', label: 'Freeze contract' },
            { key: 'void_contract', label: 'Void contract' },
        ],
    },
];

const DEFAULT_PERMISSIONS: Record<RoleKey, string[]> = {
    viewer: ['view', 'view_calendar', 'view_contracts', 'view_team', 'view_settings'],
    content_strategist: [
        'view',
        'view_calendar',
        'view_contracts',
        'view_team',
        'view_settings',
        'edit_calendar',
        'propose_calendar',
        'publish_calendar',
    ],
    content_ops_manager: [
        'view',
        'view_calendar',
        'view_contracts',
        'view_team',
        'view_settings',
        'edit_calendar',
        'propose_calendar',
        'publish_calendar',
        'manage_social',
        'view_payroll',
        'view_strikes',
    ],
    brand_admin: [
        'view',
        'view_calendar',
        'view_contracts',
        'view_team',
        'view_settings',
        'edit_calendar',
        'propose_calendar',
        'publish_calendar',
        'approve_calendar',
        'manage_social',
        'manage_team',
        'manage_contracts',
        'manage_payroll',
        'manage_strikes',
        'manage_settings',
        'freeze_contract',
        'void_contract',
        'view_payroll',
        'view_strikes',
    ],
};

type Props = {
    value: Record<string, string[]> | null | undefined;
    onChange: (next: Record<string, string[]>) => void;
    onSave: () => void;
    saving?: boolean;
};

export default function BrandManageRolePermissionsCard({ value, onChange, onSave, saving }: Props) {
    const effective = (role: RoleKey): string[] => value?.[role] ?? DEFAULT_PERMISSIONS[role];

    const toggle = (role: RoleKey, action: string) => {
        const current = effective(role);
        const next = current.includes(action) ? current.filter(a => a !== action) : [...current, action];
        const updated: Record<string, string[]> = {};
        for (const r of ROLES) {
            updated[r.key] = r.key === role ? next : effective(r.key);
        }
        onChange(updated);
    };

    const resetToDefaults = () => {
        const defaults: Record<string, string[]> = {};
        for (const r of ROLES) defaults[r.key] = [...DEFAULT_PERMISSIONS[r.key]];
        onChange(defaults);
    };

    const isCustomised = value != null;

    return (
        <ManageSurface sx={{ mt: 3 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    mb: 0.5,
                    flexWrap: 'wrap',
                    gap: 1,
                }}>
                <Box>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 15 }}>
                        Role permissions
                    </Typography>
                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12, mt: 0.25 }}>
                        Customise what each role can do. Owner always has full access.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    {isCustomised && (
                        <Box
                            component="button"
                            onClick={resetToDefaults}
                            sx={{
                                background: 'none',
                                border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: 1,
                                color: BRAND_LUXE_MUTED,
                                fontSize: 11,
                                px: 1.5,
                                py: 0.5,
                                cursor: 'pointer',
                                '&:hover': { color: '#fff', borderColor: 'rgba(255,255,255,0.3)' },
                            }}>
                            Reset to defaults
                        </Box>
                    )}
                    <ManageGoldButton onClick={onSave} disabled={saving} sx={{ py: 0.5, px: 2, fontSize: 12 }}>
                        {saving ? <CircularProgress size={14} sx={{ color: '#1a1919' }} /> : 'Save permissions'}
                    </ManageGoldButton>
                </Box>
            </Box>

            <Box sx={{ overflowX: 'auto', mt: 2.5 }}>
                <Box component="table" sx={{ borderCollapse: 'collapse', minWidth: 520, width: '100%' }}>
                    {/* Role header row */}
                    <Box component="thead">
                        <Box component="tr">
                            <Box component="th" sx={{ width: 160, pb: 1.5 }} />
                            {ROLES.map(r => (
                                <Box key={r.key} component="th" sx={{ textAlign: 'center', pb: 1.5, px: 1 }}>
                                    <Tooltip title={r.desc} placement="top">
                                        <Chip
                                            label={r.label}
                                            size="small"
                                            sx={{
                                                bgcolor: r.color,
                                                color: '#fff',
                                                fontSize: 11,
                                                fontWeight: 600,
                                                height: 24,
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                cursor: 'default',
                                            }}
                                        />
                                    </Tooltip>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Box component="tbody">
                        {PERMISSION_GROUPS.map((group, gi) => (
                            <React.Fragment key={group.group}>
                                {/* Group header */}
                                <Box component="tr">
                                    <Box
                                        component="td"
                                        colSpan={ROLES.length + 1}
                                        sx={{
                                            pt: gi === 0 ? 0 : 2.5,
                                            pb: 0.75,
                                            borderBottom: '1px solid rgba(255,255,255,0.07)',
                                        }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography
                                                sx={{
                                                    color: BRAND_LUXE_PRIMARY,
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.08em',
                                                }}>
                                                {group.group}
                                            </Typography>
                                            <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: 11 }}>
                                                — {group.description}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                {group.items.map((item, ii) => (
                                    <Box
                                        key={item.key}
                                        component="tr"
                                        sx={{
                                            bgcolor: ii % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
                                            '&:hover td': { bgcolor: 'rgba(255,201,101,0.04)' },
                                        }}>
                                        <Box
                                            component="td"
                                            sx={{ color: BRAND_LUXE_MUTED, fontSize: 12.5, py: 0.5, pr: 2 }}>
                                            {item.label}
                                        </Box>
                                        {ROLES.map(r => {
                                            const checked = effective(r.key).includes(item.key);
                                            return (
                                                <Box key={r.key} component="td" sx={{ textAlign: 'center', py: 0.5 }}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={checked}
                                                        onChange={() => toggle(r.key, item.key)}
                                                        sx={{
                                                            p: 0.5,
                                                            color: 'rgba(255,255,255,0.15)',
                                                            '&.Mui-checked': { color: BRAND_LUXE_PRIMARY },
                                                            '& .MuiSvgIcon-root': { fontSize: 18 },
                                                        }}
                                                    />
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                ))}
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            </Box>
        </ManageSurface>
    );
}
